import { useEffect, useState } from "react";
import axios from "axios";
import { siteConfig } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function cleanTitle(t) {
  const decoded = t
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
  return decoded.replace(/#\w+/g, "").replace(/\s+/g, " ").trim();
}

export default function YouTubeLatest() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/youtube/latest?limit=6`)
      .then((res) => {
        if (mounted) setVideos(res.data.videos || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, []);

  if (!loading && videos.length === 0) return null;

  return (
    <section
      id="videos"
      data-testid="youtube-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-[0.7rem] tracking-[0.35em] uppercase text-[color:var(--yicel-violet)] font-semibold">
              Videos recientes
            </span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-ink">
              Lo último en YouTube
            </h2>
          </div>
          <a
            href={siteConfig.social.youtube}
            target="_blank"
            rel="noreferrer"
            data-testid="youtube-channel-cta"
            className="text-sm text-[color:var(--yicel-muted)] hover:text-ink transition flex items-center gap-2"
          >
            Ver canal
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17 17 7M8 7h9v9" />
            </svg>
          </a>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[9/16] rounded-2xl bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {videos.map((v) => (
              <a
                key={v.id}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`youtube-video-${v.id}`}
                className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-black/5 shadow-[0_20px_40px_-25px_rgba(45,42,50,0.4)] hover:shadow-[0_30px_60px_-25px_rgba(178,164,255,0.6)] transition-all duration-300"
              >
                <img
                  src={v.thumbnail}
                  alt={v.title}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-12 w-12 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-90 group-hover:scale-100">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#2D2A32">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>

                {/* Shorts badge */}
                {v.is_short && (
                  <span className="absolute top-2 right-2 text-[0.6rem] tracking-[0.15em] uppercase bg-white/85 text-ink px-2 py-0.5 rounded-full">
                    Short
                  </span>
                )}

                {/* Title */}
                <div className="absolute bottom-0 inset-x-0 p-3">
                  <p className="text-white text-xs leading-snug line-clamp-2 font-medium">
                    {cleanTitle(v.title)}
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
